import type { ButtonHTMLAttributes, ReactNode } from "react";
import { C } from "../tokens";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "glow";
type Size = "xs" | "sm" | "md" | "lg";

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  children?: ReactNode;
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
}

const sizes: Record<Size, { padY: number; padX: number; font: number; iconGap: number }> = {
  xs: { padY: 4,  padX: 8,  font: 10, iconGap: 4 },
  sm: { padY: 6,  padX: 12, font: 11, iconGap: 6 },
  md: { padY: 9,  padX: 16, font: 12, iconGap: 8 },
  lg: { padY: 12, padX: 20, font: 13, iconGap: 10 },
};

export function Button({
  children, variant = "primary", size = "sm", icon, iconRight,
  loading = false, fullWidth = false, style, disabled, ...rest
}: Props) {
  const s = sizes[size];
  const styles: Record<Variant, { bg: string; color: string; border: string; hoverBg?: string; shadow?: string }> = {
    primary:   { bg: C.blue, color: "#fff", border: C.blue, hoverBg: "#4a92ff", shadow: "0 6px 20px rgba(45,127,249,0.35)" },
    secondary: { bg: C.surfaceHigh, color: C.text, border: C.border, hoverBg: C.surfaceMid },
    ghost:     { bg: "transparent", color: C.textSub, border: "transparent", hoverBg: C.surfaceHigh },
    danger:    { bg: C.red, color: "#fff", border: C.red, hoverBg: "#f56666", shadow: "0 6px 20px rgba(239,68,68,0.3)" },
    glow:      { bg: C.blue, color: "#fff", border: C.blue, hoverBg: "#4a92ff", shadow: `0 0 20px ${C.blue}, 0 0 40px rgba(45,127,249,0.35)` },
  };
  const st = styles[variant];
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      style={{
        background: st.bg,
        color: st.color,
        border: `1px solid ${st.border}`,
        padding: `${s.padY}px ${s.padX}px`,
        fontSize: s.font,
        fontWeight: 600,
        borderRadius: 12,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.iconGap,
        boxShadow: st.shadow,
        opacity: (disabled || loading) ? 0.5 : 1,
        cursor: (disabled || loading) ? "not-allowed" : "pointer",
        transition: "background 0.15s, transform 0.1s, box-shadow 0.2s",
        width: fullWidth ? "100%" : undefined,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (disabled || loading || !st.hoverBg) return;
        (e.currentTarget as HTMLButtonElement).style.background = st.hoverBg;
      }}
      onMouseLeave={(e) => {
        if (disabled || loading) return;
        (e.currentTarget as HTMLButtonElement).style.background = st.bg;
      }}
    >
      {loading && (
        <span
          style={{
            width: s.font, height: s.font, borderRadius: "50%",
            border: `2px solid ${st.color}`, borderTopColor: "transparent",
            animation: "cm-spin 0.8s linear infinite",
          }}
        />
      )}
      {!loading && icon}
      {children}
      {!loading && iconRight}
    </button>
  );
}
